import { Component } from '@theme/component';

/**
 * @typedef {Object} AnnouncementBarRefs
 * @property {HTMLElement} slides
 * @property {HTMLElement} viewport
 * @property {HTMLButtonElement} [closeButton]
 * @extends {Component<AnnouncementBarRefs>}
 */
class AnnouncementBarComponent extends Component {
  requiredRefs = ['slides', 'viewport'];
  #mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #resizeObserver = new ResizeObserver(() => {
    this.#syncHeight();
  });
  #slidesObserver = new MutationObserver(() => {
    this.#refreshSlides();
  });
  /** @type {number | null} */
  #rotationTimer = null;
  /** @type {number | null} */
  #bootTimer = null;
  /** @type {number | null} */
  #cleanupTimer = null;
  #currentIndex = 0;
  #previousIndex = -1;
  #isPointerInside = false;
  #isFocusInside = false;
  #bootAttempts = 0;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    if (this.#isDismissed()) {
      this.#hide();
      return;
    }

    const { signal } = this.#abortController;
    this.addEventListener('focusin', () => this.#handleFocusIn(), { signal });
    this.addEventListener('focusout', (e) => this.#handleFocusOut(/** @type {FocusEvent} */(e)), { signal });
    this.#mediaQuery.addEventListener('change', () => this.#resume(), { signal });
    this.#resizeObserver.observe(this);

    if (this.refs.slides instanceof HTMLElement) {
      this.#slidesObserver.observe(this.refs.slides, {
        childList: true,
      });
    }

    if (this.refs.closeButton instanceof HTMLButtonElement) {
      this.refs.closeButton.addEventListener('click', this.handleClose, {
        signal: this.#abortController.signal,
      });
    }

    window.addEventListener('load', this.#start, {
      signal: this.#abortController.signal,
    });
    window.addEventListener('pageshow', this.#start, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, {
      signal: this.#abortController.signal,
    });

    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();

    if (this.hidden) return;

    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#slidesObserver.disconnect();
    this.#abortController.abort();
    this.#clearBootTimer();
    this.#clearRotationTimer();
    this.#clearCleanupTimer();
  }

  handlePointerEnter = () => {
    this.#isPointerInside = true;
    this.#clearRotationTimer();
  };

  handlePointerLeave = () => {
    this.#isPointerInside = false;
    this.#resume();
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    this.#start();
  };

  /** @param {MouseEvent} event */
  handleClose = (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      this.#persistDismissal();
    } catch (error) {
      console.error(error);
    }

    this.#hide();
    this.#clearRotationTimer();
  };

  #hide() {
    this.hidden = true;
    this.style.display = 'none';
    this.setAttribute('aria-hidden', 'true');
  }

  #handleFocusIn() {
    this.#isFocusInside = true;
    this.#clearRotationTimer();
  }

  /**
   * @param {FocusEvent} event
   */
  #handleFocusOut(event) {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && this.contains(relatedTarget)) return;

    this.#isFocusInside = false;
    this.#resume();
  }

  #advance() {
    if (this.#slides.length < 2) return;

    this.#previousIndex = this.#currentIndex;
    this.#currentIndex = (this.#currentIndex + 1) % this.#slides.length;
    this.#applySlideState(this.#previousIndex, this.#currentIndex);
  }

  #refreshSlides() {
    if (this.#slides.length === 0) {
      this.#clearRotationTimer();
      return;
    }

    if (this.#currentIndex >= this.#slides.length) {
      this.#currentIndex = 0;
    }

    this.#syncHeight();
    this.#applySlideState(-1, this.#currentIndex);
  }

  #start = () => {
    this.#clearBootTimer();
    this.#clearRotationTimer();
    this.#bootAttempts = 0;

    this.#bootTimer = window.setTimeout(() => {
      this.#attemptInitialStart();
    }, 0);
  };

  #attemptInitialStart() {
    this.#clearBootTimer();

    window.requestAnimationFrame(() => {
      this.#refreshSlides();

      if (!this.#canAutoRotate()) return;
      if (this.#slides.length >= 2) {
        this.#queueNextAdvance(this.#interval);
        return;
      }

      if (this.#bootAttempts >= this.#maxBootAttempts) return;

      this.#bootAttempts += 1;
      this.#bootTimer = window.setTimeout(() => {
        this.#attemptInitialStart();
      }, this.#bootRetryDelay);
    });
  }

  #resume() {
    if (!this.#canAutoRotate()) return;
    if (this.#slides.length < 2) return;

    this.#queueNextAdvance(this.#interval);
  }

  /** @param {number} delay */
  #queueNextAdvance(delay) {
    this.#clearRotationTimer();

    this.#rotationTimer = window.setTimeout(() => {
      if (!this.#canAutoRotate()) return;
      if (this.#slides.length < 2) return;

      this.#advance();
      this.#queueNextAdvance(this.#interval);
    }, delay);
  }

  #canAutoRotate() {
    return !this.#prefersReducedMotion() && !this.#isPointerInside && !this.#isFocusInside;
  }

  /**
   * @param {number} previousIndex
   * @param {number} nextIndex
   */
  #applySlideState(previousIndex, nextIndex) {
    this.#clearCleanupTimer();

    this.#slides.forEach((slide, index) => {
      if (!(slide instanceof HTMLElement)) return;

      if (index === nextIndex) {
        slide.dataset.state = 'active';
        slide.removeAttribute('aria-hidden');
        return;
      }

      if (index === previousIndex) {
        slide.dataset.state = 'exit';
        slide.setAttribute('aria-hidden', 'true');
        return;
      }

      slide.dataset.state = 'hidden';
      slide.setAttribute('aria-hidden', 'true');
    });

    this.#cleanupTimer = window.setTimeout(() => {
      this.#slides.forEach((slide, index) => {
        if (!(slide instanceof HTMLElement)) return;
        if (index !== this.#currentIndex) {
          slide.dataset.state = 'hidden';
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    }, this.#transitionDuration);
  }

  #syncHeight() {
    if (this.#slides.length === 0) {
      this.style.removeProperty('--announcement-bar-slide-height');
      return;
    }

    let maxHeight = 0;

    for (const slide of this.#slides) {
      const previousState = slide.dataset.state;
      slide.dataset.state = 'active';
      maxHeight = Math.max(maxHeight, slide.getBoundingClientRect().height);

      if (previousState) {
        slide.dataset.state = previousState;
      } else {
        slide.removeAttribute('data-state');
      }
    }

    if (maxHeight > 0) {
      this.style.setProperty('--announcement-bar-slide-height', `${maxHeight}px`);
    }
  }

  #persistDismissal() {
    const storageKey = this.dataset.storageKey ?? '';
    if (storageKey === '' || this.dataset.designMode === 'true') return;

    const duration = Number(this.dataset.dismissDuration) || 86400000;
    window.localStorage.setItem(storageKey, String(Date.now() + duration));
  }

  #isDismissed() {
    const storageKey = this.dataset.storageKey ?? '';
    if (storageKey === '' || this.dataset.designMode === 'true') return false;

    const storedValue = window.localStorage.getItem(storageKey);
    if (!storedValue) return false;

    const expiresAt = Number(storedValue);

    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      window.localStorage.removeItem(storageKey);
      return false;
    }

    return true;
  }

  #prefersReducedMotion() {
    return this.#mediaQuery.matches;
  }

  #clearRotationTimer() {
    if (this.#rotationTimer !== null) {
      window.clearTimeout(this.#rotationTimer);
      this.#rotationTimer = null;
    }
  }

  #clearBootTimer() {
    if (this.#bootTimer !== null) {
      window.clearTimeout(this.#bootTimer);
      this.#bootTimer = null;
    }
  }

  #clearCleanupTimer() {
    if (this.#cleanupTimer !== null) {
      window.clearTimeout(this.#cleanupTimer);
      this.#cleanupTimer = null;
    }
  }

  get #slides() {
    const slides = this.refs.slides;
    if (!(slides instanceof HTMLElement)) return [];

    return [...slides.children].filter((element) => element instanceof HTMLElement);
  }

  get #interval() {
    return Math.max(Number(this.dataset.interval) || 6000, 5000);
  }

  get #bootRetryDelay() {
    return 120;
  }

  get #maxBootAttempts() {
    return 30;
  }

  get #transitionDuration() {
    return 450;
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBarComponent);
}